import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { stagger, fadeUp } from '@/lib/animations';
import {
  Truck,
  MapPin,
  Package,
  Clock,
  CheckCircle,
  AlertCircle,
  Plus,
  FileText,
  ExternalLink,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { DetailLayout, DetailList, RailCard } from '@/components/common/DetailPanels';
import {
  PageHeader,
  DataTable,
  Column,
  MetricCard,
  LoadingState,
  StatusBadge,
  KPIStrip,
} from '@/components/common';
import {
  shipmentsApi,
  ShipmentItem,
  ShipmentMilestone,
  ShipmentWithOrders,
  goodsReceiptApi,
} from '@/api/services';
import { getErrorMessage } from '@/api/client';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';

// Status mapping for StatusBadge component
const statusMap: Record<string, string> = {
  DRAFT: 'draft',
  BOOKED: 'approved',
  IN_TRANSIT: 'in_transit',
  DELIVERED: 'delivered',
  DELAYED: 'delayed',
  CANCELLED: 'cancelled',
};

export default function ShipmentDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [shipment, setShipment] = useState<ShipmentWithOrders | null>(null);
  const [milestones, setMilestones] = useState<ShipmentMilestone[]>([]);
  const [loading, setLoading] = useState(true);
  const [receiveDialogOpen, setReceiveDialogOpen] = useState(false);
  const [receivingShipment, setReceivingShipment] = useState(false);
  const [receiveFormData, setReceiveFormData] = useState({
    locationId: '',
    deliveryNoteNumber: '',
    carrierName: '',
    notes: '',
  });

  const fetchData = useCallback(async () => {
    if (!id) return;
    try {
      setLoading(true);
      const [shipmentData, milestonesData] = await Promise.all([
        shipmentsApi.getShipmentWithOrders(id),
        shipmentsApi.getMilestones(id),
      ]);
      setShipment(shipmentData);
      setMilestones(milestonesData);
    } catch (error) {
      toast.error(getErrorMessage(error));
      navigate('/shipments');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    fetchData();
  }, [id, fetchData]);

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const formatDateTime = (dateString?: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleReceiveShipment = async () => {
    if (!shipment) return;

    try {
      setReceivingShipment(true);

      const goodsReceipt = await goodsReceiptApi.createFromShipment({
        shipmentId: shipment.id,
        locationId: receiveFormData.locationId || shipment.destination?.id || '',
        deliveryNoteNumber: receiveFormData.deliveryNoteNumber,
        carrierName: receiveFormData.carrierName || shipment.carrier,
        notes: receiveFormData.notes,
      });

      toast.success(`Goods Receipt ${goodsReceipt.receiptNumber} created successfully`);
      setReceiveDialogOpen(false);

      // Goods receipts are inspected in a sheet on the list page; there is no
      // per-receipt route, so this sent every newly created receipt to the 404.
      navigate('/goods-receipt');
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setReceivingShipment(false);
    }
  };

  if (loading) {
    return <LoadingState />;
  }

  if (!shipment) {
    return null;
  }

  const items = shipment.items || [];
  const totalItems = items.reduce((sum, item) => sum + item.quantity, 0);

  const itemColumns: Column<ShipmentItem>[] = [
    {
      key: 'item',
      header: 'Item',
      render: (item) => (
        <div>
          <div className="font-medium">{item.item?.name || '-'}</div>
          <div className="text-xs leading-4 text-muted-foreground">{item.item?.sku}</div>
        </div>
      ),
    },
    {
      key: 'quantity',
      header: 'Quantity',
      render: (item) => (
        <span className="font-medium">
          {item.quantity} {item.item?.unit}
        </span>
      ),
    },
  ];

  type POItemRow = ShipmentWithOrders['purchaseOrders'][number]['items'][number];
  const poItemColumns: Column<POItemRow>[] = [
    {
      key: 'sku',
      header: 'SKU',
      // No link: the shipment payload carries only { sku, name, unit } for a
      // line's item, with no id to route to. Linking these needs the server to
      // include the item id, not a change here.
      render: (item) => <span className="font-mono text-sm">{item.item.sku}</span>,
    },
    {
      key: 'name',
      header: 'Item',
      render: (item) => <>{item.item.name}</>,
    },
    {
      key: 'quantity',
      header: 'Ordered',
      className: 'text-right',
      render: (item) => (
        <div className="text-right">
          {item.quantity} {item.item.unit}
        </div>
      ),
    },
    {
      key: 'quantityInThisShipment',
      header: 'In This Shipment',
      className: 'text-right',
      render: (item) => (
        <div className="text-right font-medium">
          {item.quantityInThisShipment} {item.item.unit}
        </div>
      ),
    },
    {
      key: 'shippedQty',
      header: 'Total Shipped',
      className: 'text-right',
      render: (item) => (
        <div className="text-right">
          {item.shippedQty} {item.item.unit}
        </div>
      ),
    },
    {
      key: 'receivedQty',
      header: 'Received',
      className: 'text-right',
      render: (item) => (
        <div className="text-right">
          {item.receivedQty} {item.item.unit}
        </div>
      ),
    },
    {
      key: 'unitPrice',
      header: 'Unit Price',
      className: 'text-right',
      render: (item) => (
        <div className="text-right">{item.unitPrice ? `$${item.unitPrice.toFixed(2)}` : '-'}</div>
      ),
    },
  ];

  const getMilestoneIcon = (milestone: string) => {
    const lower = milestone.toLowerCase();
    if (lower.includes('delivered') || lower.includes('arrived')) {
      return <CheckCircle className="h-4 w-4 text-success-500" />;
    }
    if (lower.includes('delayed') || lower.includes('issue')) {
      return <AlertCircle className="h-4 w-4 text-danger-500" />;
    }
    if (lower.includes('transit') || lower.includes('departed')) {
      return <Truck className="h-4 w-4 text-primary-500" />;
    }
    return <Clock className="h-4 w-4 text-muted-foreground" />;
  };

  return (
    <motion.main className="space-y-5" variants={stagger} initial="hidden" animate="show">
      <motion.div variants={fadeUp}>
        <PageHeader
          backLink={{ label: 'Back to Shipments', href: '/shipments' }}
          title={shipment.shipmentNumber}
          description={shipment.trackingNumber ? `Tracking: ${shipment.trackingNumber}` : undefined}
          actions={
            shipment.status === 'DELIVERED' ? (
              <Button onClick={() => setReceiveDialogOpen(true)}>
                <CheckCircle className="mr-2 h-4 w-4" />
                Receive Shipment
              </Button>
            ) : (
              <Button variant="outline">
                <Plus className="mr-2 h-4 w-4" />
                Add Milestone
              </Button>
            )
          }
        />
      </motion.div>

      {/* KPI Cards */}
      <div className="mt-6">
        <KPIStrip columns={4}>
          <MetricCard
            label="Status"
            value={shipment.status.replace('_', ' ')}
            sentiment={
              shipment.status === 'DELIVERED'
                ? 'success'
                : shipment.status === 'DELAYED'
                  ? 'danger'
                  : 'info'
            }
            icon={<Truck className="h-5 w-5" />}
          />
          <MetricCard
            label="Items"
            value={totalItems}
            sentiment="info"
            icon={<Package className="h-5 w-5" />}
          />
          <MetricCard
            label="Est. Arrival"
            value={formatDate(shipment.estimatedArrival)}
            sentiment="neutral"
          />
          <MetricCard label="Milestones" value={milestones.length} sentiment="neutral" />
        </KPIStrip>
      </div>

      {/* Route Info */}
      <motion.div variants={fadeUp} className="mt-6">
        <Card>
          <CardHeader>
            <h3 className="text-base font-semibold text-foreground">Route Information</h3>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary-50">
                  <MapPin className="h-6 w-6 text-primary-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Origin</p>
                  <p className="font-medium">{shipment.origin?.name || '-'}</p>
                  <p className="text-sm text-muted-foreground">{shipment.origin?.code}</p>
                </div>
              </div>
              <div className="flex-1 px-8">
                <div className="flex items-center gap-2">
                  <div className="h-0.5 flex-1 bg-muted" />
                  <Truck className="h-5 w-5 text-muted-foreground" />
                  <div className="h-0.5 flex-1 bg-muted" />
                </div>
                {shipment.carrier && (
                  <p className="text-center text-sm text-muted-foreground mt-2">
                    {shipment.carrier}
                  </p>
                )}
              </div>
              <div className="flex items-center gap-4">
                <div>
                  <p className="text-sm text-muted-foreground text-right">Destination</p>
                  <p className="font-medium text-right">{shipment.destination?.name || '-'}</p>
                  <p className="text-sm text-muted-foreground text-right">
                    {shipment.destination?.code}
                  </p>
                </div>
                <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-success-50">
                  <MapPin className="h-6 w-6 text-success-600" />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* The timeline, the orders it fulfils and what is on it are the work.
          Carrier, tracking number, dates and weights are reference — they used
          to hold the narrow left column while the timeline took the wide one. */}
      <motion.div variants={fadeUp} className="mt-6">
        <DetailLayout
          rail={
            <RailCard
              title="Shipment details"
              icon={<Truck className="h-4 w-4 text-muted-foreground" />}
            >
              <DetailList
                items={[
                  {
                    label: 'Status',
                    value: (
                      <StatusBadge
                        status={statusMap[shipment.status] || 'pending'}
                        label={shipment.status.replace('_', ' ')}
                      />
                    ),
                  },
                  { label: 'Carrier', value: shipment.carrier, when: !!shipment.carrier },
                  {
                    label: 'Tracking #',
                    value: <span className="font-mono">{shipment.trackingNumber}</span>,
                    when: !!shipment.trackingNumber,
                  },
                  { label: 'Est. departure', value: formatDate(shipment.estimatedDeparture) },
                  {
                    label: 'Actual departure',
                    value: formatDate(shipment.actualDeparture),
                    when: !!shipment.actualDeparture,
                  },
                  { label: 'Est. arrival', value: formatDate(shipment.estimatedArrival) },
                  {
                    label: 'Actual arrival',
                    value: formatDate(shipment.actualArrival),
                    // Arriving after the estimate is the fact this page exists
                    // to surface, and a bare date does not show it.
                    tone:
                      shipment.actualArrival &&
                      shipment.estimatedArrival &&
                      new Date(shipment.actualArrival) > new Date(shipment.estimatedArrival)
                        ? 'destructive'
                        : 'default',
                    when: !!shipment.actualArrival,
                  },
                  {
                    label: 'Total weight',
                    value: `${shipment.totalWeight} kg`,
                    when: !!shipment.totalWeight,
                  },
                  {
                    label: 'Total volume',
                    value: `${shipment.totalVolume} m³`,
                    when: !!shipment.totalVolume,
                  },
                ]}
              />
            </RailCard>
          }
          main={
            <div className="space-y-6">
              {/* Timeline */}
              <Card>
                <CardHeader>
                  <h3 className="text-base font-semibold text-foreground">Shipment Timeline</h3>
                  <CardDescription>Track progress and milestones</CardDescription>
                </CardHeader>
                <CardContent>
                  {milestones.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-8 text-center">
                      <Clock className="h-12 w-12 text-muted-foreground mb-4" />
                      <p className="text-lg font-medium">No milestones yet</p>
                      <p className="text-muted-foreground">
                        Milestones will appear here as the shipment progresses.
                      </p>
                    </div>
                  ) : (
                    <div className="relative">
                      <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-muted" />
                      <div className="space-y-6">
                        {milestones.map((milestone) => (
                          <div key={milestone.id} className="relative flex gap-4 pl-10">
                            <div className="absolute left-2 flex h-5 w-5 items-center justify-center rounded-full bg-background border-2 border-muted">
                              {getMilestoneIcon(milestone.milestone)}
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center justify-between">
                                <p className="font-medium">{milestone.milestone}</p>
                                <span className="text-sm text-muted-foreground">
                                  {formatDateTime(milestone.timestamp)}
                                </span>
                              </div>
                              {milestone.description && (
                                <p className="text-sm text-muted-foreground">
                                  {milestone.description}
                                </p>
                              )}
                              {milestone.location && (
                                <div className="flex items-center gap-1 text-sm text-muted-foreground mt-1">
                                  <MapPin className="h-3 w-3" />
                                  {milestone.location}
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Purchase Orders */}
              {shipment.purchaseOrders && shipment.purchaseOrders.length > 0 && (
                <Card>
                  <CardHeader>
                    <h3 className="text-base font-semibold text-foreground">
                      <FileText className="h-5 w-5" />
                      Purchase Orders ({shipment.purchaseOrders.length})
                    </h3>
                    <CardDescription>
                      Items in this shipment are fulfilling the following purchase orders
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    {shipment.purchaseOrders.map((po) => (
                      <div key={po.id} className="space-y-3">
                        {/* PO Header */}
                        <div className="flex items-center justify-between pb-3 border-b">
                          <div className="flex items-center gap-3">
                            <Badge variant="outline" className="font-mono">
                              {po.poNumber}
                            </Badge>
                            <span className="text-sm text-muted-foreground">
                              {po.supplier.name}
                            </span>
                            <StatusBadge
                              status={statusMap[po.status] || 'pending'}
                              label={po.status.replace('_', ' ')}
                            />
                          </div>
                          <Button variant="ghost" size="sm" asChild>
                            <Link to={`/purchase-orders/${po.id}`}>
                              View PO <ExternalLink className="ml-2 h-4 w-4" />
                            </Link>
                          </Button>
                        </div>

                        {/* PO Line Items Table */}
                        <DataTable<POItemRow>
                          pagination={false}
                          data={po.items.filter((item) => item.quantityInThisShipment > 0)}
                          columns={poItemColumns}
                        />

                        {/* PO Summary */}
                        <div className="flex justify-between text-sm text-muted-foreground pt-2">
                          <span>Order Date: {formatDate(po.orderDate)}</span>
                          <span>
                            Total: {po.currency} {po.totalAmount?.toLocaleString() || '-'}
                          </span>
                        </div>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}

              {/* Items */}
              <Card>
                <CardHeader>
                  <h3 className="text-base font-semibold text-foreground">Shipment Items</h3>
                  <CardDescription>{items.length} item(s) in this shipment</CardDescription>
                </CardHeader>
                <CardContent>
                  <DataTable
                    pagination={false}
                    data={items}
                    columns={itemColumns}
                    emptyTitle="No items"
                    emptyDescription="No items have been added to this shipment."
                  />
                </CardContent>
              </Card>
            </div>
          }
        />
      </motion.div>

      {/* Receive Shipment Dialog */}
      <Dialog open={receiveDialogOpen} onOpenChange={setReceiveDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Receive Shipment</DialogTitle>
            <DialogDescription>
              Create a Goods Receipt to formally receive and inspect this shipment.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label>Receiving Location</Label>
              <Input value={shipment?.destination?.name || ''} disabled />
            </div>

            <div>
              <Label>Delivery Note Number</Label>
              <Input
                value={receiveFormData.deliveryNoteNumber}
                onChange={(e) =>
                  setReceiveFormData({ ...receiveFormData, deliveryNoteNumber: e.target.value })
                }
                placeholder="Optional"
              />
            </div>

            <div>
              <Label>Carrier Name</Label>
              <Input
                value={receiveFormData.carrierName}
                onChange={(e) =>
                  setReceiveFormData({ ...receiveFormData, carrierName: e.target.value })
                }
                placeholder={shipment?.carrier || 'Optional'}
              />
            </div>

            <div>
              <Label>Notes</Label>
              <Textarea
                value={receiveFormData.notes}
                onChange={(e) => setReceiveFormData({ ...receiveFormData, notes: e.target.value })}
                rows={3}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setReceiveDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleReceiveShipment} disabled={receivingShipment}>
              {receivingShipment ? 'Creating...' : 'Create Goods Receipt'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </motion.main>
  );
}
